// pages/stations/KitchenDashboardPage.jsx — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftRight,
  Check,
  CheckCircle2,
  ChefHat,
  LogOut,
  RefreshCw,
  Timer,
  Truck,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { useRealtimeConnection, useRealtimeEvent } from "@/lib/realtime";
import { applyRealtimeEvent, hasLocalSnapshot, loadDashboardBundle } from "@/lib/localDb";
import { mergeDeptOrders, sameOrderIdentity, replaceStationOrders } from "@/lib/mergeState";
import { localWrite } from "@/lib/eventEngine";
import { EventType } from "@/lib/events";
import { PrepExecutionPanel, PrepModeButton } from "@/components/prep/PrepExecutionPanel";
import { fireAndForgetMesh } from "@/lib/lanMesh";
import { MeshStatusPill } from "@/components/MeshStatusPill";
import { useConnectivity } from "@/hooks/useConnectivity";
import { KitchenTimeAgo, LongWaitTicketBadge, StationDepartmentPicker, ticketWaitMinutes, useStationClock, useStationDepartmentScope } from "@/pages/stations/stationShared";
import { ChimeVolumeControl, playKitchenChime, useLoginChimeOnMount } from "@/app/chimes";
import {
  BACKEND_URL,
  clearSession,
  confirmSession,
  confirmSessionDeadThen,
  getSession,
  http,
  isTransientCloudError,
  shouldKickOutOn401,
  useOfflineSessionUpgrade,
} from "@/app/core";
import { beginLogoutSuppression, exitFullscreenIfActive, FullscreenToggleButton, ThemeToggleButton, useCoalescedUpdater } from "@/app/fullscreen";
import { useAutoLogoutOnHidden } from "@/app/fragments";


export function KitchenDashboardPage() {
  const connectivity = useConnectivity(); // Session 30.8 — LAN/internet tri-state
  const stationNow = useStationClock(); // Session 30.21 — long-wait flags
  const [longWaitMinutes, setLongWaitMinutes] = useState(15);
  const navigate = useNavigate();
  useLoginChimeOnMount();
  const [session, setSessionState] = useState(() => getSession("kitchen"));
  useOfflineSessionUpgrade("kitchen", session, setSessionState); // Session 46
  useEffect(() => { try { confirmSession("kitchen"); } catch (_) { /* noop */ } }, []);
  const headers = useMemo(() => (session?.token ? { Authorization: `Bearer ${session.token}` } : null), [session]);
  // Session 63 — which kitchen department this screen operates.
  const deptScope = useStationDepartmentScope("kitchen", headers);
  // Session 41 — station prep execution (full-screen, kitchen sheets only).
  const [prepMode, setPrepMode] = useState(false);
  // Session 30.23 — "Keep until delivered". Business-level setting
  // (authoritative copy lives on the business doc, returned by
  // /kitchen/orders and pushed via the business_settings_changed WS
  // event). Mirrored in localStorage so a cold reopen paints the right
  // mode instantly. A ref shadows the state so memoised callbacks
  // (fetchOrders) and async handlers never read a stale value.
  const KEEP_KEY = "nfcs:kitchen:keep-until-delivered";
  const [keepUntilDelivered, setKeepUntilDelivered] = useState(() => {
    try { return localStorage.getItem(KEEP_KEY) === "1"; } catch (_) { return false; }
  });
  const keepUntilDeliveredRef = useRef(keepUntilDelivered);
  const applyKeepSetting = useCallback((value) => {
    const v = !!value;
    keepUntilDeliveredRef.current = v;
    setKeepUntilDelivered(v);
    try { localStorage.setItem(KEEP_KEY, v ? "1" : "0"); } catch (_) { /* noop */ }
  }, []);
  const [savingKeep, setSavingKeep] = useState(false);
  // Session 64 — TEAM MODE for the kitchen (mirror of the Session 55 bar
  // flag). Several cooks share one screen: a ticket must be claimed ("I've
  // got this") before it can be marked ready, so two people never plate the
  // same order. Business-wide (server), mirrored locally for cold start.
  const TEAM_KEY = "nfcs:kitchen:team-mode";
  const [teamMode, setTeamMode] = useState(() => {
    try { return localStorage.getItem(TEAM_KEY) === "1"; } catch (_) { return false; }
  });
  const teamModeRef = useRef(teamMode);
  const applyTeamMode = useCallback((value) => {
    const v = !!value;
    teamModeRef.current = v;
    setTeamMode(v);
    try { localStorage.setItem(TEAM_KEY, v ? "1" : "0"); } catch (_) { /* noop */ }
  }, []);
  const [savingTeam, setSavingTeam] = useState(false);
  const [claimBusy, setClaimBusy] = useState(null); // order id in flight
  // Item-level helpers shared by the optimistic + WS paths (ON mode only).
  const _isKitchenDeliveredAll = (items) => {
    const live = (items || []).filter((it) => it && !it.voided);
    // Session 73 — a ticket whose every live line was pruned (all voided
    // elsewhere) has nothing left for this station: treat as done.
    return live.every((it) => String(it.status || "").toLowerCase() === "delivered");
  };
  const _isAwaitingDelivery = (items) => {
    const live = (items || []).filter((it) => it && !it.voided);
    return live.length > 0 && live.every((it) => ["ready", "delivered"].includes(String(it.status || "").toLowerCase()));
  };
  const _applyItemStatuses = (order, snapshot, full = false) => {
    // Merge item statuses from a WS `all_items`/`items` snapshot by stable id.
    const byId = new Map((snapshot || []).map((it) => [String(it.id || ""), it]).filter(([k]) => k !== ""));
    if (byId.size === 0) return order;
    // Session 73 — `full` means the snapshot is the server's WHOLE live
    // item list (`all_items`, voided lines already excluded). A local line
    // with a stable id that is missing from it was voided on another
    // device (event missed) — drop it, otherwise the ticket stays on the
    // screen "pending" forever after everything else was delivered.
    const items = (order.items || []).flatMap((it) => {
      const id = String(it.id || "");
      const s = byId.get(id);
      if (full && id !== "" && !s) return [];
      return [s && s.status ? { ...it, status: s.status } : it];
    });
    return { ...order, items, kitchen_awaiting_delivery: _isAwaitingDelivery(items) };
  };
  // Local-first paint: seed orders synchronously from the last successful
  // fetch cached in sessionStorage so a cold reopen renders tickets
  // instantly. The network refresh then reconciles.
  const [orders, setOrdersRaw] = useState(() => {
    try {
      const raw = sessionStorage.getItem("nfcs:kitchen:last-orders");
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed?.orders) ? parsed.orders : [];
    } catch (_) { return []; }
  });
  // Session 63 — every write to the ticket list passes through the
  // department scope so WS / mesh / snapshot / cache paths can never leak
  // another kitchen's lines onto this screen (and item indexes keep
  // matching the server's department-scoped list).
  // Session 72 — ONE SOURCE OF TRUTH. Every local / WS write to the list is
  // stamped here; `fetchOrders` writes through `setOrdersRaw` and, when the
  // screen has been quiet for QUIET_BEFORE_AUTHORITATIVE_MS before the
  // request went out, lets the server list REPLACE local state.
  const lastActivityAtRef = useRef(0);
  const QUIET_BEFORE_AUTHORITATIVE_MS = 2500;
  const forceAuthoritativeRef = useRef(false); // Session 73 — set by the safety-net poll
  const setOrders = useCallback((updater) => {
    lastActivityAtRef.current = Date.now();
    setOrdersRaw((prev) => deptScope.scopeOrders(typeof updater === "function" ? updater(prev) : updater));
    // Depends on the memoised scopeOrders fn only — the deptScope object
    // itself is rebuilt per render and would churn every subscriber.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptScope.scopeOrders]);
  const [loading, setLoading] = useState(() => {
    // If we primed from cache, skip the skeleton entirely.
    try {
      const raw = sessionStorage.getItem("nfcs:kitchen:last-orders");
      return !(raw && JSON.parse(raw)?.orders?.length);
    } catch (_) { return true; }
  });
  const [readyingId, setReadyingId] = useState(null);
  // Session 27.101 — Per-order item-selection map (order_id → Set<item_id>).
  // Enables partial-ready — chef ticks the items ready and sends
  // just those. Cleared as soon as the send succeeds.
  const [selectedItems, setSelectedItems] = useState({});
  const _toggleItem = (orderId, itemKey) => {
    setSelectedItems((prev) => {
      const set = new Set(prev[orderId] || []);
      if (set.has(itemKey)) set.delete(itemKey); else set.add(itemKey);
      return { ...prev, [orderId]: set };
    });
  };
  const _markSelectedReady = async (orderId) => {
    const keys = Array.from(selectedItems[orderId] || []);
    if (!keys.length) { toast.error("Select at least one item"); return; }
    const indexes = keys.map((k) => parseInt(k, 10)).filter((n) => !Number.isNaN(n));
    const indexSet = new Set(indexes);
    const orderData = orders.find((o) => o.id === orderId);
    // Session 31.2 — count exactly the rows the chef can tick (live, not
    // yet ready) and send stable item ids alongside the positions so the
    // server never flags the wrong line after a void shifted the list.
    const visibleCount = (orderData?.items || []).filter(
      (it) => it && !it.voided && it.status !== "ready" && it.status !== "delivered"
    ).length;
    const selectedIds = indexes.map((i) => orderData?.items?.[i]?.id).filter(Boolean);
    const isFullSend = indexes.length >= visibleCount;

    // Session 27.127 — Optimistic UI update first (instant feedback),
    // then await the API call so errors are reliably caught. If the API
    // fails the UI self-heals via refetch.
    if (keepUntilDeliveredRef.current) {
      // Session 30.23 — Keep-until-delivered: never drop the card here.
      // Flip the sent items to "ready" and let the ticket move to the
      // "awaiting delivery" state once nothing is left to prepare.
      setOrders((prev) => prev.map((o) => {
        if (o.id !== orderId) return o;
        const updatedItems = (o.items || []).map((it, idx) =>
          indexSet.has(idx) ? { ...it, status: "ready", ready_at: new Date().toISOString() } : it
        );
        return { ...o, items: updatedItems, kitchen_awaiting_delivery: _isAwaitingDelivery(updatedItems) };
      }));
    } else if (isFullSend) {
      recentlyReadyRef.current.set(orderId, Date.now());
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
    } else {
      setOrders((prev) => prev.map((o) => {
        if (o.id !== orderId) return o;
        const updatedItems = (o.items || []).map((it, idx) =>
          indexSet.has(idx) ? { ...it, status: "ready" } : it
        );
        const stillPending = updatedItems.some(
          (it) => it.status !== "ready" && it.status !== "delivered"
        );
        return stillPending ? { ...o, items: updatedItems } : null;
      }).filter(Boolean));
    }
    setSelectedItems((prev) => { const n = { ...prev }; delete n[orderId]; return n; });

    // Session 30.34 — LAN-first ping for PARTIAL sends too. Until now
    // only the whole-order "Ready" button pinged the mesh / queued an
    // offline event, so on a router-only network a station could not
    // send a single item ahead of the rest — staff only heard about
    // the ticket once everything was marked. Mirror markReady():
    // fire the mesh envelope (with the selected items + partial flag)
    // before the REST call, and fall back to the offline event queue
    // when the cloud is unreachable or the ticket is LAN-only.
    const stationItems = (orderData?.items || []);
    const selectedPayloadItems = indexes
      .map((i) => stationItems[i])
      .filter(Boolean)
      .map((it, n) => ({
        id: it.id || null,
        name: it.name,
        quantity: Number(it.quantity || 1),
        department: it.department || "kitchen",
        station_index: indexes[n],
      }));
    try {
      fireAndForgetMesh({
        change_id: (crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        event_type: "order_status_changed",
        ts: new Date().toISOString(),
        _mesh_hint: "ready",
        payload: {
          order_id: orderId,
          status: "ready",
          department: "kitchen",
          order_number: orderData?.order_number || null,
          table_number: orderData?.table_number || null,
          items: selectedPayloadItems,
          partial: !isFullSend,
        },
      }, { targetRole: "*" });
    } catch (_) { /* mesh best-effort */ }
    const queueOffline = async () => {
      await localWrite({
        type: EventType.ORDER_STATUS_CHANGED,
        payload: {
          order_id: orderId,
          order_change_id: orderData?._change_id || null,
          status: "ready",
          department: "kitchen",
          ready_at: new Date().toISOString(),
          item_indexes: indexes,
          item_ids: selectedPayloadItems.map((it) => it.id).filter(Boolean),
          items: selectedPayloadItems,
          partial: !isFullSend,
        },
        session,
      });
    };
    try {
      if (String(orderId).startsWith("local-")) {
        await queueOffline();
        toast.message("Ready saved — this ticket is still syncing and will update the cloud automatically.");
        return;
      }
      await http.post(`/kitchen/orders/${orderId}/items-ready`,
        { item_indexes: indexes, item_ids: selectedIds, ...deptScope.scopeBody() }, { headers });
      setTimeout(fetchOrders, 600);
    } catch (error) {
      if (isTransientCloudError(error)) {
        try {
          await queueOffline();
          toast.message("Ready saved on this device — the cloud is unreachable or busy right now; it will sync automatically.");
          return;
        } catch (_) { /* fall through to the revert path */ }
      }
      if (isFullSend && !keepUntilDeliveredRef.current) recentlyReadyRef.current.delete(orderId);
      toast.error(error.response?.data?.detail || "Could not mark items ready — please try again");
      fetchOrders();
    }
  };
  // Track which order IDs we've already shown so the chime only fires
  // when a NEW ticket lands (not on every poll). Initialised lazily on
  // the first successful fetch so we don't ding for orders already
  // present at login.
  const knownOrderIdsRef = useRef(null);
  // Session 27.11 — ghost-order suppression: IDs we just marked ready
  // stay filtered out of fetchOrders responses for 15s (see fetchOrders).
  const recentlyReadyRef = useRef(new Map());

  useEffect(() => {
    if (!session?.token) navigate("/kitchen", { replace: true });
  }, [session, navigate]);

  // Session 27.140 — Lock+dirty pattern for Kitchen (same as Staff/Runner).
  const kitchenFetchInFlightRef = useRef(false);
  const kitchenFetchDirtyRef = useRef(false);

  // Session 28 — Sequence tracking + gap-based reconciliation for Kitchen.
  const kitchenLastSeqRef = useRef(0);
  const kitchenReconciliationTimerRef = useRef(null);
  const kitchenFetchRef = useRef(null);
  const scheduleKitchenReconciliation = useCallback(() => {
    if (kitchenReconciliationTimerRef.current) return;
    kitchenReconciliationTimerRef.current = setTimeout(() => {
      kitchenReconciliationTimerRef.current = null;
      if (kitchenFetchRef.current) kitchenFetchRef.current();
    }, 300);
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!headers) return;
    // Session 63 — wait until the department scope is settled (picker
    // answered / single department / lookup failed) so the first list we
    // paint is already the right department's.
    if (!deptScope.readyRef.current) return;
    if (kitchenFetchInFlightRef.current) {
      kitchenFetchDirtyRef.current = true;
      return;
    }
    kitchenFetchInFlightRef.current = true;
    kitchenFetchDirtyRef.current = false;
    const fetchStartedAt = Date.now();
    const forceAuthoritative = forceAuthoritativeRef.current === true;
    forceAuthoritativeRef.current = false;
    try {
      const response = await http.get("/kitchen/orders", { headers, params: deptScope.scopeParams() });
      if (Number(response.data?.long_wait_minutes) > 0) setLongWaitMinutes(Number(response.data.long_wait_minutes));
      // Session 30.23 — server copy of the keep-until-delivered setting
      // is authoritative (another kitchen screen may have flipped it).
      if (typeof response.data?.keep_until_delivered === "boolean"
          && response.data.keep_until_delivered !== keepUntilDeliveredRef.current) {
        applyKeepSetting(response.data.keep_until_delivered);
      }
      // Session 64 — business-wide Team mode flag rides along with the list.
      if (typeof response.data?.kitchen_team_mode === "boolean" && response.data.kitchen_team_mode !== teamModeRef.current) {
        applyTeamMode(response.data.kitchen_team_mode);
      }
      let next = response.data.orders || [];
      // Session 28b — Capture event_seq for merge, but DO NOT advance
      // kitchenLastSeqRef. Only WS events should advance the seq.
      const serverSeq = (typeof response.data.event_seq === "number") ? response.data.event_seq : 0;
      // Ghost-order suppression. When kitchen marks an order ready we
      // optimistically remove it, but the very next fetch (or a WS
      // refresh burst) can race the backend's status update and
      // re-surface the order for a moment.  Suppressed for 15 s —
      // enough headroom for slow-network round-trips while still
      // allowing a genuine new order on the same ID to come through.
      const recently = recentlyReadyRef.current;
      const cutoff = Date.now() - 15000;
      for (const [id, ts] of Array.from(recently.entries())) {
        if (ts < cutoff) recently.delete(id);
      }
      if (recently.size) {
        next = next.filter((o) => !recently.has(o.id));
      }
      // Cache for the next cold-start "local-first" paint.
      try {
        sessionStorage.setItem(
          "nfcs:kitchen:last-orders",
          JSON.stringify({ orders: next, at: Date.now() }),
        );
      } catch (_) { /* quota — ignore */ }
      // New-ticket detection — only chime AFTER the initial baseline is
      // captured. Skips re-chiming on the first load when several
      // tickets may already exist.
      const nextIds = new Set(next.map((o) => o.id));
      if (knownOrderIdsRef.current === null) {
        knownOrderIdsRef.current = nextIds;
      } else {
        let foundNew = false;
        for (const o of next) {
          // Session 30.23 — tickets already cooked (awaiting delivery)
          // are never "new work"; don't chime when they (re)surface.
          if (o.kitchen_awaiting_delivery) continue;
          if (!knownOrderIdsRef.current.has(o.id)) { foundNew = true; break; }
        }
        if (foundNew) playKitchenChime();
        knownOrderIdsRef.current = nextIds;
      }
      // Session 72 — quiet screen → the server list is the whole truth.
      // Busy screen (a tick or a WS delta in the last few seconds) → the
      // Session 28b monotonic merge for this one refresh.
      const authoritative = forceAuthoritative || (fetchStartedAt - (lastActivityAtRef.current || 0) > QUIET_BEFORE_AUTHORITATIVE_MS);
      setOrdersRaw((prev) => deptScope.scopeOrders(
        authoritative
          ? replaceStationOrders(prev, next, recentlyReadyRef.current)
          : mergeDeptOrders(prev, next, serverSeq, kitchenLastSeqRef.current, recentlyReadyRef.current)
      ));
    } catch (error) {
      if (error.response?.status === 401) {
        // Session 27.110 — require two consecutive 401s in a short
        // window before we clear the session. A single transient
        // 401 (Cloudflare hiccup, backend micro-restart, WS token
        // race) should never yank the kitchen staff off the ticket
        // board mid-service.
        if (!shouldKickOutOn401("kitchen")) return;
        const token = session?.token;
        confirmSessionDeadThen("kitchen", token, () => {
          clearSession("kitchen", "server_401_confirmed");
          setSessionState(null);
        });
        return;
      }
      // Session 27.24 — Silence transient failures (Cloudflare 520s,
      // network hiccups). The list keeps showing the last-known
      // tickets and the 4s poll will self-heal within seconds. A red
      // toast every 4s during a bad-network moment turned the kitchen
      // into an alarm ringing 15 times a minute.
      const status = error.response?.status;
      const isTransient = !status || status >= 500;
      if (!isTransient) {
        toast.error(error.response?.data?.detail || "Could not load tickets");
      }
    } finally {
      setLoading(false);
      kitchenFetchInFlightRef.current = false;
      if (kitchenFetchDirtyRef.current) {
        kitchenFetchDirtyRef.current = false;
        setTimeout(() => fetchOrders(), 50);
      }
    }
    // Session 69 routing fix relies on this exact re-run set: stable
    // scope pieces only (readyRef / scopeParams), never the whole
    // deptScope object or session.token (headers already covers it);
    // applyTeamMode is a stable setter-wrapper.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers, applyKeepSetting, deptScope.readyRef, deptScope.scopeParams, deptScope.scopeOrders, setOrders]);
  kitchenFetchRef.current = fetchOrders;

  // Session 63 — once the department is chosen (or the scope settles),
  // re-scope anything primed from cache and pull that department's list.
  useEffect(() => {
    if (!deptScope.readyRef.current) return;
    setOrders((prev) => prev);
    knownOrderIdsRef.current = null;
    fetchOrders();
  }, [deptScope.selected, deptScope.loadingDepts, fetchOrders, setOrders, deptScope.readyRef]);

  // Session 30.23 — Toggle handler. Optimistic flip → PATCH the business
  // setting → refetch so already-ready tickets appear/disappear at once.
  const toggleKeepUntilDelivered = async (value) => {
    if (!headers || savingKeep) return;
    const prevValue = keepUntilDeliveredRef.current;
    applyKeepSetting(value);
    // Ghost suppression only exists for the OFF workflow; clear it so a
    // ticket readied seconds ago isn't hidden for 15 s after turning ON.
    if (value) recentlyReadyRef.current.clear();
    setSavingKeep(true);
    try {
      await http.patch("/kitchen/settings", { kitchen_keep_until_delivered: !!value }, { headers });
      toast.success(value
        ? "Ready tickets now stay on screen until a runner or waiter marks them delivered."
        : "Ready tickets now leave the screen as soon as you mark them ready.");
      fetchOrders();
    } catch (error) {
      applyKeepSetting(prevValue);
      toast.error(error.response?.data?.detail || "Could not save the setting — please try again");
    } finally {
      setSavingKeep(false);
    }
  };

  // Session 64 — Team mode toggle (business-wide; every kitchen screen follows).
  const toggleTeamMode = async (value) => {
    if (!headers || savingTeam) return;
    const prevValue = teamModeRef.current;
    applyTeamMode(value);
    setSavingTeam(true);
    try {
      await http.patch("/kitchen/settings", { kitchen_team_mode: !!value }, { headers });
      toast.success(value
        ? "Team mode on — tap “I've got this” on a ticket before cooking it. Everyone else sees it turn amber and leaves it alone."
        : "Team mode off — tickets go straight to “Mark ready”.");
      fetchOrders();
    } catch (error) {
      applyTeamMode(prevValue);
      toast.error(error.response?.data?.detail || "Could not save the setting — please try again");
    } finally {
      setSavingTeam(false);
    }
  };

  // Session 64 — "I've got this" / "Not me". Optimistic: the ticket turns
  // amber on THIS screen instantly; the server broadcasts to the others.
  const claimOrder = async (orderId, claimed) => {
    if (!headers || claimBusy) return;
    setClaimBusy(orderId);
    const nowIso = new Date().toISOString();
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, kitchen_claimed: claimed, kitchen_claimed_at: claimed ? nowIso : null } : o)));
    try {
      await http.post(`/kitchen/orders/${orderId}/${claimed ? "claim" : "unclaim"}`, {}, { headers });
    } catch (error) {
      if (!isTransientCloudError(error)) {
        setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, kitchen_claimed: !claimed, kitchen_claimed_at: claimed ? null : nowIso } : o)));
        toast.error(error.response?.data?.detail || "Could not update the ticket — please try again");
      } else {
        toast.message("Cloud is busy — claim shown on this screen; other screens will catch up when it reconnects.");
      }
    } finally {
      setClaimBusy(null);
    }
  };

  useEffect(() => {
    // Session 27.2 — LOCAL-FIRST KITCHEN PAINT.
    //
    // Before the /kitchen/orders network call, hydrate open kitchen
    // tickets straight from IndexedDB. If a snapshot has been
    // installed we render instantly with the last-known queue instead
    // of blocking on wifi. The realtime WS + interval poll keep the
    // list fresh.
    let cancelled = false;
    (async () => {
      try {
        const primed = await hasLocalSnapshot();
        if (!primed || cancelled) return;
        const bundle = await loadDashboardBundle();
        if (!bundle || cancelled) return;
        // Kitchen ticket criteria matches the server:
        //   - status is pending / confirmed / preparing / ready
        //   - not voided
        //   - the order carries at least one item routed to the kitchen
        //     (fallback: no routing info → show it anyway so nothing is
        //     accidentally hidden on older data).
        const KITCHEN_STATUSES = new Set([
          "pending", "confirmed", "preparing", "ready", "sent_to_kitchen",
        ]);
        // Session 69 — the snapshot holds EVERY order of the business
        // (kitchen + bar + direct-serve). Painting it unfiltered put
        // drinks tickets on the kitchen (and food on the bar) and, once
        // the backend was slow, the reconciliation never pruned them.
        // Keep only this station's live lines, mirror the server's
        // ready/keep-until-delivered rule, and never overwrite a list the
        // server has already delivered (IndexedDB can be slower than the
        // network on a busy tablet).
        if (knownOrderIdsRef.current !== null) return;
        const keep = keepUntilDeliveredRef.current;
        const localOrders = (bundle.collections?.orders || [])
          .filter((o) => o && o.status !== "voided" && KITCHEN_STATUSES.has(o.status) && !o.awaiting_staff_confirmation)
          .filter((o) => keep || !o.kitchen_ready_at)
          .map((o) => {
            const items = (o.items || []).filter((it) => deptScope.lineMatches(it, "kitchen"));
            if (!items.length) return null;
            return { ...o, items, kitchen_item_count: items.reduce((n, it) => n + (Number(it.quantity) || 1), 0) };
          })
          .filter(Boolean)
          .sort((a, b) => {
            const ta = new Date(a.created_at || 0).getTime();
            const tb = new Date(b.created_at || 0).getTime();
            return ta - tb; // oldest first (FIFO for kitchen)
          });
        if (localOrders.length > 0 && !cancelled && knownOrderIdsRef.current === null) {
          setOrders(localOrders);
          setLoading(false);
        }
      } catch (_) { /* best-effort */ }
    })();
    fetchOrders();
    // Session 28 — Safety-net poll only. WS drives the fast path.
    // Session 73 — 6 s, and the safety-net poll ALWAYS lets the server list
    // win (a busy screen never went "quiet" during a rush, so a ticket the
    // server had already dropped — delivered / voided elsewhere — could sit
    // on the board until the next sign-in).
    const RECONCILIATION_INTERVAL = 6000;
    const interval = setInterval(() => { forceAuthoritativeRef.current = true; fetchOrders(); }, RECONCILIATION_INTERVAL);
    return () => { cancelled = true; clearInterval(interval); if (kitchenReconciliationTimerRef.current) { clearTimeout(kitchenReconciliationTimerRef.current); kitchenReconciliationTimerRef.current = null; } };
    // Poll + reconcile cadence is keyed to fetchOrders alone on purpose:
    // deptScope/setOrders are already folded into fetchOrders' identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOrders]);

  useRealtimeConnection({ baseUrl: BACKEND_URL, token: session?.token });
  const queueOrdersUpdate = useCoalescedUpdater(setOrders);   // Session 70.E — one render per WS burst
  // Session 72 — screen wake / tab focus / network back → pull the truth now.
  useEffect(() => {
    if (!session?.token) return undefined;
    const refresh = () => { if (document.visibilityState !== "hidden" && kitchenFetchRef.current) kitchenFetchRef.current(); };
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", refresh); window.removeEventListener("online", refresh); };
  }, [session?.token]);
  useRealtimeEvent([
    "hello", "state_changed",
    "order_created", "order_updated", "order_status_changed",
    "order_confirmed_by_staff", "business_settings_changed", "kitchen_claim_changed",
  ], (msg) => {
    // Session 30 — Fresh WS connection: resync seq + reconcile.
    if ((msg?.event_type || msg?.type) === "hello") {
      const serverSeq = Number((msg?.payload || {}).seq);
      kitchenLastSeqRef.current = Number.isFinite(serverSeq) ? serverSeq : 0;
      scheduleKitchenReconciliation();
      return;
    }
    // Session 72 — a write finished anywhere (any device, any endpoint):
    // one debounced, server-cached reconcile so this screen converges
    // within ~0.5 s even when the delta carried no dedicated event.
    if ((msg?.event_type || msg?.type) === "state_changed") {
      if (typeof msg?.seq === "number" && msg.seq > kitchenLastSeqRef.current) kitchenLastSeqRef.current = msg.seq;
      scheduleKitchenReconciliation();
      return;
    }
    // Session 64 — another cook claimed / released a ticket.
    if ((msg?.event_type || msg?.type) === "kitchen_claim_changed") {
      const p = msg?.payload || {};
      if (p.order_id) {
        queueOrdersUpdate((prev) => prev.map((o) => (o.id === p.order_id ? { ...o, kitchen_claimed: !!p.claimed, kitchen_claimed_at: p.claimed ? (o.kitchen_claimed_at || new Date().toISOString()) : null } : o)));
      }
      return;
    }
    // Session 30.23 — Another kitchen screen (or the owner) flipped the
    // keep-until-delivered setting: mirror it here and reconcile.
    if ((msg?.event_type || msg?.type) === "business_settings_changed") {
      const v = (msg?.payload || {}).kitchen_keep_until_delivered;
      if (typeof v === "boolean" && v !== keepUntilDeliveredRef.current) {
        applyKeepSetting(v);
        if (v) recentlyReadyRef.current.clear();
        scheduleKitchenReconciliation();
      }
      const tm = (msg?.payload || {}).kitchen_team_mode;
      if (typeof tm === "boolean" && tm !== teamModeRef.current) applyTeamMode(tm);
      return;
    }
    try { applyRealtimeEvent(msg); } catch (_) { /* noop */ }

    // Session 28 — Event-driven state architecture for Kitchen.
    // Direct state mutations from WS events; HTTP fetch only on gaps.
    try {
      const eventType = msg?.event_type || msg?.type;
      const p = msg?.payload || msg || {};
      const orderId = String(p.order_id || p.id || "");
      const seq = typeof msg?.seq === "number" ? msg.seq : 0;

      // Gap detection + dedup
      if (seq > 0) {
        const lastSeq = kitchenLastSeqRef.current;
        if (lastSeq > 0 && seq <= lastSeq) return; // Already processed
        if (lastSeq > 0 && seq > lastSeq + 1) {
          console.warn(`[kitchen-ws] gap detected: last=${lastSeq} received=${seq} — scheduling reconciliation`);
          scheduleKitchenReconciliation();
        }
        kitchenLastSeqRef.current = Math.max(kitchenLastSeqRef.current, seq);
      }

      // ── Session 31.13 — VOID: drop voided lines / the whole ticket at once ──
      // Fired by /void, /void-line(s) and staff decline. `voided_item_ids`
      // names the lines that left the order; `kitchen_live` is how many
      // undelivered kitchen lines remain (0 → nothing left to cook).
      if (eventType === "order_status_changed" && p.voided === true && orderId) {
        const matchIds = new Set([orderId, p.local_id, p._change_id].filter(Boolean).map(String));
        const matches = (o) => o && (matchIds.has(String(o.id)) || (o._change_id && matchIds.has(String(o._change_id))));
        const voidedIds = new Set((Array.isArray(p.voided_item_ids) ? p.voided_item_ids : []).map(String).filter(Boolean));
        const dropTicket = p.full_void === true || p.status === "voided" || p.status === "cancelled" || p.kitchen_live === 0;
        const snapQty = new Map((Array.isArray(p.all_items) ? p.all_items : []).filter((it) => it && it.id && !it.voided).map((it) => [String(it.id), Number(it.quantity)]));
        queueOrdersUpdate((prev) => prev.map((o) => {
          if (!matches(o)) return o;
          if (dropTicket) return null;
          let changed = false;
          const items = (o.items || []).filter((it) => it && !it.voided && !voidedIds.has(String(it.id || ""))).map((it) => {
            const q = snapQty.get(String(it.id || ""));
            if (Number.isFinite(q) && q > 0 && q !== Number(it.quantity)) { changed = true; return { ...it, quantity: q }; } // partial-quantity void
            return it;
          });
          if (items.length === 0) return null;
          if (!changed && items.length === (o.items || []).length) return o;
          return { ...o, items, kitchen_item_count: items.reduce((n, it) => n + (Number(it.quantity) || 1), 0), kitchen_awaiting_delivery: _isAwaitingDelivery(items) };
        }).filter(Boolean));
        if (dropTicket) {
          matchIds.forEach((id) => { if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(id); });
          recentlyReadyRef.current.set(orderId, Date.now()); // keep the safety-net poll from re-surfacing it
        } else if (voidedIds.size === 0 && snapQty.size === 0) {
          scheduleKitchenReconciliation(); // legacy lines without ids — let the server list decide
        }
        return;
      }

      // ── order_created / order_confirmed_by_staff: Add ticket if kitchen-relevant ──
      if ((eventType === "order_created" || eventType === "order_confirmed_by_staff") && p.order) {
        const fullOrder = p.order;
        // Session 31.20 — gated customer orders ("Have staff confirm order
        // before departments") must not appear until a waiter releases them.
        if (fullOrder && fullOrder.awaiting_staff_confirmation) return;
        if (fullOrder && fullOrder.id) {
          // Check if order has kitchen items (Session 63 — judged against
          // this screen's department scope, not the literal "kitchen").
          const kitchenItems = (fullOrder.items || []).filter((it) => deptScope.lineMatches(it, "kitchen"));
          if (kitchenItems.length > 0 && !recentlyReadyRef.current.has(fullOrder.id)) {
            const kitchenOrder = {
              ...fullOrder,
              items: kitchenItems,
              kitchen_item_count: kitchenItems.reduce((n, it) => n + (Number(it.quantity) || 1), 0),
              _ws_added_at: Date.now(), // Session 69 — bounds the merge grace (mergeDeptOrders)
            };
            // Session 30.26 / 31.24 — A ticket that arrived earlier over
            // the LAN mesh (optimistic `local-…` id) is the SAME logical
            // order as this cloud copy. Match on the full identity set
            // (id / _local_id / _change_id) — not just `_change_id`, which
            // could differ between the mesh and sync copies and leave the
            // duplicate on screen until a manual refresh.
            const replacesLocal = orders.some((o) => o && o.id !== fullOrder.id && sameOrderIdentity(o, fullOrder));
            queueOrdersUpdate((prev) => {
              if (prev.some((o) => o.id === fullOrder.id)) return prev; // Idempotent
              const idx = prev.findIndex((o) => o && o.id !== fullOrder.id && sameOrderIdentity(o, fullOrder));
              if (idx >= 0) {
                // Never downgrade a server (real-id) ticket back to a
                // LAN-optimistic (`local-…`) one when the mesh copy lands
                // after the cloud copy — just drop the incoming duplicate.
                const incomingIsLocal = String(fullOrder.id || "").startsWith("local-");
                const existingIsLocal = String(prev[idx].id || "").startsWith("local-");
                if (incomingIsLocal && !existingIsLocal) return prev;
                const next = prev.slice(); next[idx] = kitchenOrder; return next;
              }
              return [...prev, kitchenOrder];
            });
            // Chime for new ticket
            if (!replacesLocal && knownOrderIdsRef.current !== null && !knownOrderIdsRef.current.has(fullOrder.id)) {
              playKitchenChime();
            }
            if (knownOrderIdsRef.current) knownOrderIdsRef.current.add(fullOrder.id);
          }
        } else if (!p.order) {
          scheduleKitchenReconciliation();
        }
      }

      // ── order_status_changed: handle ready/delivered/completed ──
      if (eventType === "order_status_changed" && orderId && keepUntilDeliveredRef.current) {
        // Session 30.23 — KEEP-UNTIL-DELIVERED mode. Ready events never
        // remove a ticket; only authoritative delivery/completion does.
        // Item statuses are merged from the WS snapshot (all_items) by
        // stable id so partial deliveries shrink the ticket precisely.
        const snapshot = Array.isArray(p.all_items) && p.all_items.length
          ? p.all_items
          : (Array.isArray(p.items) ? p.items : []);
        const localOrder = orders.find((o) => o.id === orderId) || null;
        const isKitchenReady = p.status === "ready" && p.department === "kitchen";
        // Session 31.6 — a station ack only counts as final when it covered
        // the WHOLE station (`partial` false / `kitchen_done` true). A partial
        // "Mark delivered" used to yank the ticket while items were still
        // being prepared.
        const isFinal = p.completed === true || p.status === "completed"
          || p.status === "voided" || p.status === "cancelled"
          || p.kitchen_done === true
          || (p.acknowledged === "kitchen" && p.partial === false);
        if (isKitchenReady) {
          if (!localOrder) {
            scheduleKitchenReconciliation(); // readied elsewhere, we never had it
          } else if (!p.partial) {
            queueOrdersUpdate((prev) => prev.map((o) => {
              if (o.id !== orderId) return o;
              const items = (o.items || []).map((it) =>
                String(it.status || "").toLowerCase() === "delivered"
                  ? it
                  : { ...it, status: "ready", ready_at: it.ready_at || p.ready_at || new Date().toISOString() }
              );
              return { ...o, items, kitchen_awaiting_delivery: true };
            }));
          } else {
            const merged = _applyItemStatuses(localOrder, snapshot, snapshot === p.all_items);
            if (merged === localOrder) scheduleKitchenReconciliation(); // legacy items without ids
            else queueOrdersUpdate((prev) => prev.map((o) => (o.id === orderId ? merged : o)));
          }
        } else if (isFinal) {
          queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
          if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(orderId);
        } else if (localOrder && snapshot.length
          && (p.status === "delivered" || p.acknowledged === "kitchen" || (Array.isArray(p.delivered_item_ids) && p.delivered_item_ids.length))) {
          const merged = _applyItemStatuses(localOrder, snapshot, snapshot === p.all_items);
          if (merged === localOrder) {
            scheduleKitchenReconciliation();
          } else if (_isKitchenDeliveredAll(merged.items)) {
            queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
            if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(orderId);
          } else {
            queueOrdersUpdate((prev) => prev.map((o) => (o.id === orderId ? merged : o)));
          }
        } else if (localOrder && p.status === "delivered") {
          // Session 30.26 — Delivery without an item snapshot (runner
          // working offline over the LAN): everything that was ready is
          // now delivered. Apply locally so the ticket clears even when
          // the reconciliation fetch can't reach the cloud.
          const items = (localOrder.items || []).map((it) =>
            String(it.status || "").toLowerCase() === "ready" ? { ...it, status: "delivered" } : it
          );
          if (_isKitchenDeliveredAll(items)) {
            queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
            if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(orderId);
          } else {
            queueOrdersUpdate((prev) => prev.map((o) => (o.id === orderId ? { ...o, items, kitchen_awaiting_delivery: _isAwaitingDelivery(items) } : o)));
          }
          scheduleKitchenReconciliation();
        }
      } else if (eventType === "order_status_changed" && orderId) {
        if (p.status === "ready" && (p.department === "kitchen" || p.acknowledged === "kitchen")) {
          if (!p.partial) {
            recentlyReadyRef.current.set(orderId, Date.now());
            queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
            if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(orderId);
          } else {
            // Session 29 — Use stable item IDs, filter out empty strings
            const readyItemIds = new Set(
              (p.items || []).map((it) => String(it.id || "")).filter((x) => x !== "")
            );
            if (readyItemIds.size > 0) {
              queueOrdersUpdate((prev) => prev.map((o) => {
                if (o.id !== orderId) return o;
                const updatedItems = (o.items || []).map((it) => {
                  if (readyItemIds.has(String(it.id || ""))) return { ...it, status: "ready" };
                  return it;
                });
                const stillPending = updatedItems.some((it) => it.status !== "ready" && it.status !== "delivered");
                return stillPending ? { ...o, items: updatedItems } : null;
              }).filter(Boolean));
            } else {
              // Legacy fallback: no stable IDs, schedule reconciliation
              scheduleKitchenReconciliation();
            }
          }
        }
        // Session 31.6 — Delivery / ack events. NEVER drop a ticket that
        // still has kitchen items to prepare: a runner delivering the
        // items that were ready, or staff acking a partial banner, used to
        // remove the whole ticket. Decide from the explicit station flag
        // or the item snapshot; only hard completion removes blindly.
        const isHardFinal = p.completed === true || p.status === "completed" || p.status === "voided" || p.status === "cancelled";
        const touchesDelivery = p.status === "delivered" || p.acknowledged === "kitchen"
          || (Array.isArray(p.delivered_item_ids) && p.delivered_item_ids.length > 0);
        if (isHardFinal || p.kitchen_done === true || (p.acknowledged === "kitchen" && p.partial === false)) {
          queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
          if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(orderId);
        } else if (touchesDelivery) {
          const snapshot = Array.isArray(p.all_items) && p.all_items.length ? p.all_items : (Array.isArray(p.items) ? p.items : []);
          const localOrder = orders.find((o) => o.id === orderId) || null;
          if (localOrder && snapshot.length) {
            const merged = _applyItemStatuses(localOrder, snapshot, snapshot === p.all_items);
            const live = (merged.items || []).filter((it) => it && !it.voided);
            const stationFinished = live.length > 0 && live.every((it) => ["ready", "delivered"].includes(String(it.status || "").toLowerCase()));
            if (stationFinished) {
              queueOrdersUpdate((prev) => prev.filter((o) => o.id !== orderId));
              if (knownOrderIdsRef.current) knownOrderIdsRef.current.delete(orderId);
            } else if (merged !== localOrder) {
              queueOrdersUpdate((prev) => prev.map((o) => (o.id === orderId ? merged : o)));
            }
          } else if (localOrder) {
            scheduleKitchenReconciliation(); // no snapshot — let the server list decide
          }
        }
      }

      if (eventType === "order_updated") {
        scheduleKitchenReconciliation();
      }
      // Session 72 — every order event is followed by one debounced,
      // server-cached reconcile: the delta above paints instantly, the
      // snapshot makes this screen identical to every other one.
      if (eventType === "order_created" || eventType === "order_status_changed" || eventType === "order_confirmed_by_staff") {
        scheduleKitchenReconciliation();
      }
    } catch (e) { console.warn("[kitchen-ws] direct mutation error:", e?.message || e); }
  });

  const markReady = async (orderId) => {
    // Session 30.23 — In keep-until-delivered mode the ticket stays on
    // screen (flipped to the awaiting-delivery state) and must NOT be
    // ghost-suppressed on the next fetch.
    const keep = keepUntilDeliveredRef.current;
    // Session 27.11 — remember this ID so the next fetch/poll doesn't
    // re-surface it while the backend/status change is propagating.
    if (!keep) recentlyReadyRef.current.set(orderId, Date.now());
    // Session 27.8 — LAN-first "ready" ping. Fire the mesh event
    // BEFORE waiting on the REST call so any paired staff tablet
    // gets the chime + toast within milliseconds, even if the cloud
    // is currently slow / down. The REST call still runs and is
    // what actually mutates the order status server-side; the mesh
    // event is a hot-path notification only.
    try {
      const order = orders.find((o) => o.id === orderId) || null;
      const envelope = {
        change_id: (crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        event_type: "order_status_changed",
        ts: new Date().toISOString(),
        _mesh_hint: "ready",
        payload: {
          order_id: orderId,
          status: "ready",
          department: "kitchen",
          order_number: order?.order_number || null,
          table_number: order?.table_number || null,
          items: order?.items || [],
        },
      };
      // Session 30.1 — Broadcast to ALL peers ("*") instead of only
      // role="staff". The mesh role of a device is derived from its
      // stored sessions and can lag the dashboard actually on screen
      // (e.g. a tablet that switched roles without logging out), and
      // Runner shares the staff session while Retail has its own —
      // role-targeted delivery silently dropped ready pings for
      // them. Receivers already filter by event_type + payload, so
      // fan-out is safe and cheap (a few hundred bytes per peer).
      fireAndForgetMesh(envelope, { targetRole: "*" });
    } catch (_) { /* mesh best-effort */ }
    // Session 27.127 — Optimistic-first: remove card immediately,
    // then await API so errors are reliably caught.
    if (keep) {
      const nowIso = new Date().toISOString();
      setOrders((prev) => prev.map((o) => {
        if (o.id !== orderId) return o;
        const items = (o.items || []).map((it) =>
          String(it.status || "").toLowerCase() === "delivered" ? it : { ...it, status: "ready", ready_at: nowIso }
        );
        return { ...o, items, kitchen_awaiting_delivery: true };
      }));
    } else {
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
    }
    setReadyingId(null);
    // Session 30.26 — Ticket that arrived over the LAN mesh and is not
    // on the server yet (`local-…` id). The REST call can only 404;
    // queue the status change instead — the backend resolves the
    // optimistic id via `_local_id` / the order's change_id and defers
    // (retries) until the staff device's order_created has landed.
    const localOrderRef = orders.find((o) => o.id === orderId) || null;
    const isLocalOnly = String(orderId).startsWith("local-");
    try {
      if (isLocalOnly) {
        await localWrite({
          type: EventType.ORDER_STATUS_CHANGED,
          payload: {
            order_id: orderId,
            order_change_id: localOrderRef?._change_id || null,
            status: "ready",
            department: "kitchen",
            ready_at: new Date().toISOString(),
          },
          session,
        });
        toast.message("Ready saved — this ticket is still syncing and will update the cloud automatically.");
        return;
      }
      await http.post(`/kitchen/orders/${orderId}/ready`, deptScope.scopeBody(), { headers });
      setTimeout(fetchOrders, 600);
    } catch (error) {
      if (isTransientCloudError(error)) {
        // Session 30.1 — Network unreachable (offline mode). The LAN
        // mesh ping above already notified staff; queue the status
        // change in the local event queue so it forwards to the
        // Coordinator over LAN and syncs to the cloud (deduped by
        // change_id) the moment internet returns. Keep the optimistic
        // removal — the kitchen is the source of truth right now.
        try {
          await localWrite({
            type: EventType.ORDER_STATUS_CHANGED,
            payload: {
              order_id: orderId,
              order_change_id: localOrderRef?._change_id || null,
              status: "ready",
              department: "kitchen",
              ready_at: new Date().toISOString(),
            },
            session,
          });
          toast.message("Ready saved on this device — the cloud is unreachable or busy right now; it will sync automatically.");
          return;
        } catch (_) { /* fall through to the revert path */ }
      }
      recentlyReadyRef.current.delete(orderId);
      toast.error(error.response?.data?.detail || "Could not mark ready — please try again");
      fetchOrders();
    }
  };

  const logout = async () => {
    beginLogoutSuppression();
    await exitFullscreenIfActive();
    const tokenSnapshot = session?.token;
    clearSession("kitchen", "user_logout");
    setSessionState(null);
    navigate("/kitchen", { replace: true });
    try {
      if (tokenSnapshot) {
        await http.post("/auth/logout", {}, { headers: { Authorization: `Bearer ${tokenSnapshot}` } });
      }
    } catch {
      // no-op — local session already cleared.
    }
  };

  // Auto-logout when the dashboard leaves the foreground long enough to
  // suggest the user navigated away (home button, app switch). Brief
  // device locks within graceMs keep the session.
  useAutoLogoutOnHidden(logout, { enabled: !!session?.token, graceMs: 60000 });

  if (!session?.token) return null;

  // Session 63 — multi-kitchen venues: ask which department this screen
  // runs before showing any ticket. Asked on every load by design.
  if (deptScope.needsPick) {
    return (
      <StationDepartmentPicker
        station="kitchen"
        departments={deptScope.departments}
        businessName={session.business?.name}
        onSelect={deptScope.choose}
        onLogout={logout}
      />
    );
  }
  const deptLabel = deptScope.selected || (Array.isArray(deptScope.departments) && deptScope.departments.length === 1 ? deptScope.departments[0].name : null);
  const canSwitchDept = Array.isArray(deptScope.departments) && deptScope.departments.length > 1;

  // Session 30.23 — Split tickets into "still cooking" vs "ready, waiting
  // for a runner/waiter" (the latter only exists in keep-until-delivered
  // mode). A ticket counts as awaiting delivery when every non-delivered
  // kitchen item is already ready.
  const awaitingDeliveryOrders = keepUntilDelivered
    ? orders.filter((o) => {
        const live = (o.items || []).filter((it) => it && !it.voided && String(it.status || "").toLowerCase() !== "delivered");
        return live.length > 0 && live.every((it) => String(it.status || "").toLowerCase() === "ready");
      })
    : [];
  const awaitingIds = new Set(awaitingDeliveryOrders.map((o) => o.id));
  const inProgressOrders = keepUntilDelivered ? orders.filter((o) => !awaitingIds.has(o.id)) : orders;

  return (
    <main className="min-h-screen bg-background px-4 py-5 sm:px-6 sm:py-7">
      {/* ---- Kitchen dashboard root ---- */}
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-5">
        <header className="flex flex-col gap-3 rounded-[28px] border border-border bg-card p-5 shadow-[0_1px_0_rgba(0,0,0,0.04)] sm:flex-row sm:items-center sm:justify-between sm:p-6" data-testid="kitchen-header">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--primary))] text-primary-foreground">
              <ChefHat className="h-7 w-7" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-[hsl(var(--primary))] text-primary-foreground">Kitchen station</Badge>
                {deptLabel ? (
                  <Badge variant="outline" className="border-[hsl(var(--primary))]/40 text-foreground" data-testid="kitchen-department-badge">{deptLabel}</Badge>
                ) : null}
              </div>
              <h1 className="mt-2 font-['Space_Grotesk'] text-3xl font-semibold tracking-tight sm:text-4xl">
                {session.business?.name || "Kitchen Dashboard"}
              </h1>
              <p className="text-sm text-muted-foreground" data-testid="kitchen-ticket-summary">
                {deptScope.loadingDepts && orders.length === 0
                  ? "Loading departments…"
                  : orders.length === 0
                  ? "All clear — kitchen is caught up. ✨"
                  : keepUntilDelivered
                    ? `${inProgressOrders.length} in progress · ${awaitingDeliveryOrders.length} awaiting delivery`
                    : `${orders.length} active ${orders.length === 1 ? "ticket" : "tickets"}`}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canSwitchDept ? (
              <Button
                variant="outline"
                className="rounded-xl"
                onClick={() => { setOrdersRaw([]); setLoading(true); deptScope.reset(); }}
                data-testid="kitchen-switch-department-button"
                title="Choose a different kitchen department for this screen"
              >
                <ArrowLeftRight className="h-4 w-4" />
                <span className="hidden sm:inline">Switch department</span>
              </Button>
            ) : null}
            {/* Session 64 — Team mode toggle (business-wide), mirrors the bar. */}
            <label
              className={`flex h-10 cursor-pointer select-none items-center gap-2 rounded-xl border px-3 text-sm font-medium transition-colors ${teamMode ? "border-amber-500/60 bg-amber-500/10 text-amber-800 dark:text-amber-300" : "border-border bg-card text-foreground hover:bg-secondary"}`}
              title="Several cooks on one screen? When on, tap “I've got this” on a ticket before cooking it — it turns amber on every kitchen screen so nobody else plates the same order. “Mark ready” appears only after a ticket is claimed."
              data-testid="kitchen-team-mode-toggle"
            >
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Team mode</span>
              <span className="sm:hidden">Team</span>
              <Switch
                checked={teamMode}
                disabled={savingTeam}
                onCheckedChange={toggleTeamMode}
                aria-label="Team mode — claim tickets before cooking them"
                data-testid="kitchen-team-mode-switch"
              />
            </label>
            {/* Session 30.23 — Keep-until-delivered toggle (business-wide). */}
            <label
              className={`flex h-10 cursor-pointer select-none items-center gap-2 rounded-xl border px-3 text-sm font-medium transition-colors ${keepUntilDelivered ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-border bg-card text-foreground hover:bg-secondary"}`}
              title="When on, tickets you mark ready stay on this screen (highlighted) until a runner or waiter marks them delivered."
              data-testid="kitchen-keep-until-delivered-toggle"
            >
              <Truck className="h-4 w-4" />
              <span className="hidden sm:inline">Keep until delivered</span>
              <span className="sm:hidden">Keep</span>
              <Switch
                checked={keepUntilDelivered}
                disabled={savingKeep}
                onCheckedChange={toggleKeepUntilDelivered}
                aria-label="Keep orders on screen until delivered"
                data-testid="kitchen-keep-until-delivered-switch"
              />
            </label>
            <PrepModeButton http={http} headers={headers} station="kitchen" active={prepMode} onClick={() => setPrepMode((v) => !v)} />
            <MeshStatusPill />
            <ThemeToggleButton />
            <FullscreenToggleButton lockDown />
            <ChimeVolumeControl role="kitchen" />
            {/* Session 30.8 — data-only refresh; never touches the LAN
                mesh. Hidden when this installed device lost its LAN peers
                (use the mesh pill's Reconnect LAN instead). */}
            {connectivity.refreshAllowed ? (
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => { fetchOrders(); }}
              data-testid="kitchen-refresh-button"
              title="Refresh orders"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            ) : null}
            <Button variant="outline" className="rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive" onClick={logout} data-testid="kitchen-logout-button">
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </header>
        {prepMode ? (
          <PrepExecutionPanel http={http} headers={headers} station="kitchen" session={session} onBack={() => setPrepMode(false)} />
        ) : (
          <>

        {loading && orders.length === 0 ? (
          <Card className="rounded-[28px] border-border bg-card"><CardContent className="p-8"><Skeleton className="h-32 w-full" /></CardContent></Card>
        ) : null}

        {!loading && orders.length === 0 ? (
          <Card className="rounded-[28px] border-2 border-dashed border-border bg-card">
            <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/10">
                <CheckCircle2 className="h-10 w-10 text-emerald-500" />
              </div>
              <p className="font-['Space_Grotesk'] text-2xl font-semibold">All caught up!</p>
              <p className="max-w-md text-sm text-muted-foreground">
                New tickets will appear here automatically. The page refreshes every 5 seconds.
              </p>
            </CardContent>
          </Card>
        ) : null}

        {(() => {
          const renderTicket = (order) => {
            // Session 27.103 — Hide items already marked ready so the
            // ticket shrinks to just the pending items. When every
            // item is ready the backend will set `kitchen_ready_at`
            // and the whole ticket disappears from /kitchen/orders on
            // the next fetch (800ms).
            // Session 30.23 — In keep-until-delivered mode ready items
            // stay visible (highlighted); only delivered ones are hidden.
            const visibleItems = (order.items || []).filter(
              (it) => (it && it.status !== "delivered" && (keepUntilDelivered || it.status !== "ready"))
            );
            if (visibleItems.length === 0) return null;
            const isAwaiting = keepUntilDelivered && awaitingIds.has(order.id);
            const waitMin = ticketWaitMinutes(order, stationNow);
            const isLongWait = !isAwaiting && waitMin >= longWaitMinutes;
            // Session 64 — Team mode: a claimed ticket is AMBER everywhere so
            // the other cooks leave it alone.
            const isClaimed = teamMode && !isAwaiting && !!(order.kitchen_claimed || order.kitchen_claimed_at);
            return (
            <Card
              key={order.id}
              className={`overflow-hidden rounded-[28px] border-2 shadow-md transition hover:shadow-lg ${isAwaiting ? "border-emerald-500 bg-card ring-2 ring-emerald-400/30" : isClaimed ? "border-amber-500 bg-amber-50 ring-2 ring-amber-400/40 dark:bg-amber-950/30" : isLongWait ? "border-rose-500 bg-card ring-4 ring-rose-400/40 animate-[nfcsLongWaitPulse_1.6s_ease-in-out_infinite]" : "border-border bg-card hover:border-[hsl(var(--accent))]"}`}
              data-testid={`kitchen-ticket-${order.id}`}
              data-long-wait={isLongWait ? "true" : undefined}
              data-awaiting-delivery={isAwaiting ? "true" : undefined}
              data-claimed={isClaimed ? "true" : undefined}
            >
              {isLongWait && !isClaimed ? <style>{`@keyframes nfcsLongWaitPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(244,63,94,0.45); } 50% { box-shadow: 0 0 0 10px rgba(244,63,94,0); } }`}</style> : null}
              <div className={`flex items-center justify-between px-5 py-4 ${isAwaiting ? "bg-emerald-600 text-white" : isClaimed ? "bg-amber-500 text-white" : isLongWait ? "bg-rose-600 text-white" : "bg-[hsl(var(--primary))] text-primary-foreground"}`}>
                <div>
                  <p className="text-xs uppercase tracking-wider opacity-80">Table</p>
                  <p className="font-['Space_Grotesk'] text-4xl font-bold leading-none" data-testid={`kitchen-ticket-table-${order.id}`}>
                    {order.table_number || "—"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider opacity-80">Order</p>
                  <p className="font-mono text-base font-semibold">{order.order_number}</p>
                  {isLongWait ? (
                    <div className="mt-1"><LongWaitTicketBadge minutes={waitMin} testId={`kitchen-ticket-long-wait-${order.id}`} /></div>
                  ) : (
                    <p className="mt-1 inline-flex items-center gap-1 text-xs opacity-80">
                      <Timer className="h-3 w-3" />
                      <KitchenTimeAgo from={order.created_at} />
                    </p>
                  )}
                </div>
              </div>
              {isAwaiting ? (
                <div
                  className="flex items-center gap-2 border-b border-emerald-500/30 bg-emerald-500/15 px-5 py-2 text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300"
                  data-testid={`kitchen-ticket-awaiting-delivery-${order.id}`}
                >
                  <Truck className="h-4 w-4" />
                  Ready — awaiting delivery
                </div>
              ) : isClaimed ? (
                <div
                  className="flex items-center justify-between gap-2 border-b border-amber-500/40 bg-amber-500/20 px-5 py-2 text-xs font-bold uppercase tracking-wider text-amber-900 dark:text-amber-200"
                  data-testid={`kitchen-ticket-claimed-${order.id}`}
                >
                  <span className="inline-flex items-center gap-2"><Users className="h-4 w-4" /> Claimed — being made</span>
                  <button
                    type="button"
                    className="rounded-md px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal underline-offset-2 hover:underline disabled:opacity-50"
                    onClick={() => claimOrder(order.id, false)}
                    disabled={claimBusy === order.id}
                    title="Release this ticket so someone else can take it"
                    data-testid={`kitchen-unclaim-button-${order.id}`}
                  >
                    Not me
                  </button>
                </div>
              ) : null}
              <CardContent className="space-y-3 p-5">
                <div className="space-y-2">
                  {(order.items || []).map((item, idx) => {
                    // Session 27.102 — use the array index as the selection
                    // key. Backend `items-ready` expects `item_indexes[]`
                    // because order.items lack stable ids across writes.
                    const itemId = String(idx);
                    const isSelected = (selectedItems[order.id] || new Set()).has(itemId);
                    const alreadyReady = item.status === "ready" || item.status === "delivered";
                    // Session 27.103 — Hide items already marked ready so
                    // the ticket only shows what still needs to be made.
                    // Session 30.23 — unless keep-until-delivered is on:
                    // ready (not yet delivered) items stay, highlighted.
                    const showReadyRow = keepUntilDelivered && item.status === "ready";
                    if (item.voided) return null; // Session 31.2 — never show voided lines
                    if (alreadyReady && !showReadyRow) return null;
                    return (
                    <div key={`${order.id}-${idx}`} className={`flex items-start gap-3 rounded-2xl border ${alreadyReady ? "border-emerald-500/50 bg-emerald-500/10" : "border-border bg-secondary/40"} px-4 py-3`} data-item-status={item.status || "pending"}>
                      {/* Session 27.101 — Per-item selection checkbox. */}
                      {alreadyReady ? (
                        <CheckCircle2 className="mt-2 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-label="Ready" />
                      ) : (
                      <input
                        type="checkbox"
                        className="mt-2 h-5 w-5 accent-[hsl(var(--accent))]"
                        checked={isSelected}
                        onChange={() => _toggleItem(order.id, itemId)}
                        data-testid={`kitchen-item-check-${itemId}`}
                      />
                      )}
                      <div className="flex h-10 min-w-10 items-center justify-center rounded-xl bg-[hsl(var(--accent))] px-2 font-['Space_Grotesk'] text-lg font-bold text-[hsl(var(--accent-foreground))]">
                        {item.quantity}×
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold leading-tight">{item.name}{alreadyReady ? <span className="ml-2 inline-flex items-center rounded-md bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" data-testid={`kitchen-item-ready-pill-${order.id}-${idx}`}>Ready</span> : null}</p>
                        {Array.isArray(item.selected_sides) && item.selected_sides.length ? (
                          <ul className="mt-1 space-y-0.5 text-xs text-foreground" data-testid="kitchen-line-sides">
                            {item.selected_sides.map((s, sIdx) => (
                              <li key={`ks-${idx}-${sIdx}`} className="flex items-center gap-1">
                                <span aria-hidden className="text-[hsl(var(--accent))]">+</span>
                                <span className="font-medium">{s.name}</span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {Array.isArray(item.prep_selections) && item.prep_selections.length ? (
                          <ul className="mt-1 space-y-0.5 text-xs text-foreground" data-testid="kitchen-line-prep">
                            {item.prep_selections.map((p, pIdx) => (
                              <li key={`kp-${idx}-${pIdx}`} className="flex items-center gap-1">
                                <span aria-hidden className="text-[hsl(var(--accent))]">*</span>
                                <span className="font-semibold">{p.label}:</span>
                                <span className="uppercase tracking-wide">{p.choice}</span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {item.note ? (
                          <p className="mt-1 rounded-lg bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-900 dark:bg-amber-900/40 dark:text-amber-100" data-testid={`kitchen-line-note-${idx}`}>
                            <span className="mr-1">📝</span>{item.note}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    );
                  })}
                </div>
                {/* Session 27.103 — Single color-changing action button.
                    - No items selected: emerald "Order Ready" (marks the
                      whole ticket ready).
                    - 1+ items selected: blue "Send N Selected" (partial
                      ready — only those items go to staff, the rest stay
                      on this ticket).
                    Consolidating to one button prevents mis-taps. */}
                {isAwaiting ? (
                  <div
                    className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-emerald-500/50 bg-emerald-500/10 text-sm font-semibold text-emerald-700 dark:text-emerald-300"
                    data-testid={`kitchen-awaiting-delivery-${order.id}`}
                    role="status"
                  >
                    <Truck className="h-5 w-5" />
                    Waiting for a runner / waiter to deliver
                  </div>
                ) : teamMode && !isClaimed ? (
                  // Session 64 — step 1 of 2: claim it. "Order Ready" only
                  // appears once the ticket is claimed (amber).
                  <Button
                    className="h-14 w-full rounded-2xl bg-amber-500 text-base font-semibold text-white shadow-md hover:bg-amber-600 disabled:opacity-60"
                    onClick={() => claimOrder(order.id, true)}
                    disabled={claimBusy === order.id}
                    data-testid={`kitchen-claim-button-${order.id}`}
                  >
                    <Users className="h-5 w-5" />
                    {claimBusy === order.id ? "Claiming…" : "I've got this"}
                  </Button>
                ) : (() => {
                  const selectedCount = (selectedItems[order.id] && selectedItems[order.id].size) || 0;
                  const isPartial = selectedCount > 0;
                  const label = readyingId === order.id
                    ? "Sending…"
                    : isPartial
                      ? `Send ${selectedCount} Selected`
                      : "Order Ready";
                  const colorClass = isPartial
                    ? "bg-blue-600 hover:bg-blue-700"
                    : isClaimed ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700";
                  return (
                    <Button
                      className={`h-14 w-full rounded-2xl ${colorClass} text-base font-semibold text-white shadow-md disabled:opacity-60`}
                      onClick={() => (isPartial ? _markSelectedReady(order.id) : markReady(order.id))}
                      disabled={readyingId === order.id}
                      data-testid={`kitchen-ready-button-${order.id}`}
                    >
                      <Check className="h-5 w-5" />
                      {label}
                    </Button>
                  );
                })()}
              </CardContent>
            </Card>
            );
          };
          return (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="kitchen-in-progress-grid">
                {inProgressOrders.map(renderTicket)}
              </div>
              {keepUntilDelivered && awaitingDeliveryOrders.length > 0 ? (
                <section className="flex flex-col gap-4" data-testid="kitchen-awaiting-delivery-section">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white">
                      <Truck className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="font-['Space_Grotesk'] text-xl font-semibold leading-tight">Ready — awaiting delivery</h2>
                      <p className="text-xs text-muted-foreground">
                        {awaitingDeliveryOrders.length} {awaitingDeliveryOrders.length === 1 ? "ticket" : "tickets"} cooked and waiting for a runner or waiter. They clear automatically once marked delivered.
                      </p>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="kitchen-awaiting-delivery-grid">
                    {awaitingDeliveryOrders.map(renderTicket)}
                  </div>
                </section>
              ) : null}
            </>
          );
        })()}
          </>
        )}
      </div>
    </main>
  );
}
